import { FC, useState, MouseEvent } from 'react';
import { SelectChangeEvent } from '@mui/material';
import Cookies from 'js-cookie';
import ConfirmationDialog from 'src/components/DialogBox/ConfirmationDialog/ConfirmationDialog';
import {
  actionButtons,
  apiUrls,
  dataStorageKeywords,
  customerStatusOptions,
  customerDialogTitles,
  customerDialogSubtitles,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { capitalizeFirstLetter } from 'src/utils/text.utils';
import { Address, Customer } from 'src/models/Management.interface';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { useFormik } from 'formik';
import {
  customerAddressInitialValues,
  customerInitialValues,
} from 'src/utils/initialValues.utils';
import {
  customerAddressValidationSchema,
  customerValidationSchema,
} from 'src/utils/validationScheme.utils';
import ViewDialog from 'src/components/DialogBox/POBox/ViewBox';
import AddressBox from 'src/components/DialogBox/AddressBox/AddressBox';
import TextField from 'src/components/InputFields/TextField';
import MobileField from 'src/components/InputFields/MobileField';
import CustomDialog from 'src/components/DialogBox/FormBox/CustomBox';
import { AddressType } from 'src/models/DialogBox.interface';
import CountryPicker from 'src/components/InputFields/CountryPicker';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { calculateDialogHeight } from 'src/utils/dialog.utils';
import { CustomerStatus } from 'src/models/enums/CustomerStatus.enum';
import {
  ActionMenuButton,
  ActionMenuPopover,
  MenuItemsWrapper,
  BoxWithMarginBottom,
  FlexColumnBox,
  FormFieldRow,
  Box300Width,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';

const EMPTY_CUSTOMER: Customer = {
  id: '',
  name: '',
  customerCode: '',
  organizationName: '',
  telephoneNo: '',
  email: '',
  status: CustomerStatus.ACTIVE,
  suspensionReason: '',
};

const CustomerActionMenuButton: FC<{
  rowId: any;
  customerCode: string;
  status: CustomerStatus;
  suspensionReason?: string;
  fetchCustomers: () => void;
  deleteCustomer: (customerId: string) => void;
}> = ({
  rowId,
  customerCode,
  status,
  suspensionReason = '',
  fetchCustomers,
  deleteCustomer,
}) => {
  const { showSnackbar } = StyledSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [defaultDialogOpen, setDefaultDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editAddressOpen, setEditAddressOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    null
  );
  const [newStatus, setNewStatus] = useState<CustomerStatus>(status);
  const [internalSuspensionReason, setInternalSuspensionReason] =
    useState<string>(suspensionReason || '');
  const [customerAddresses, setCustomerAddresses] = useState<AddressType[]>([]);
  const [addAddressDialogOpen, setAddAddressDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const getAddressDialogHeight = () =>
    calculateDialogHeight(addressFormik, [
      ['contactPersonName', 'contactNo'],
      ['addressLine1', 'addressLine2'],
      ['city', 'postCode'],
      ['country'],
    ]) + 100;

  const handleStatusChange = (updatedStatus: CustomerStatus) => {
    setNewStatus(updatedStatus);
  };

  const handleClose = () => setAnchorEl(null);

  const handleClick = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const handleEditDialogOpen = async (customer: Customer) => {
    setSelectedCustomer(customer);
    try {
      const response = await axiosTokenInstance.get(
        apiUrls.GET_CUSTOMER_BY_ID,
        {
          params: { id: rowId },
        }
      );

      formik.setValues({
        name: response.data.name,
        customerCode: response.data.customerCode,
        organizationName: response.data.organizationName,
        countryCode: response.data.countryCode,
        telephoneNo: response.data.phoneNumber,
        email: response.data.email,
        status: response.data.status,
        reason: response.data.suspensionReason,
      });
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
    setEditDialogOpen(true);
  };

  const handleEditFormSubmit = async () => {
    if (!selectedCustomer) {
      return;
    }
    try {
      const response = await axiosTokenInstance.put(apiUrls.EDIT_CUSTOMER, {
        id: selectedCustomer.id,
        name: formik.values.name,
        customerCode: formik.values.customerCode,
        organizationName: formik.values.organizationName,
        telephoneNo: `+${formik.values.countryCode} ${formik.values.telephoneNo}`,
        email: formik.values.email,
      });
      setEditDialogOpen(false);
      fetchCustomers();
      showSnackbar(response.data.message, 'success');
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedCustomer(null);
    formik.resetForm();
  };

  const handleAddressEditOpen = async (addressId: string) => {
    try {
      const response = await axiosTokenInstance.get(apiUrls.GET_ADDRESS_BY_ID, {
        params: { id: addressId },
      });

      editFormik.setValues({
        id: addressId,
        contactPersonName: response.data.contactPersonName,
        addressLine1: response.data.addressLine1,
        addressLine2: response.data.addressLine2,
        postCode: response.data.postCode,
        city: response.data.city,
        country: response.data.country,
        countryCode: response.data.countryCode,
        contactNo: response.data.number,
        isDefault: response.data.isDefault,
      });

      setEditAddressOpen(true);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditAddressSubmit = async () => {
    if (!selectedCustomer) return;

    try {
      const addressData = createAddressData(editFormik.values);
      const response = await axiosTokenInstance.put(apiUrls.EDIT_ADDRESS, {
        id: editFormik.values.id,
        ...addressData,
      });
      setEditAddressOpen(false);
      showSnackbar(response.data.message, 'success');
      setCustomerAddresses((prevAddresses) =>
        prevAddresses.map((address) =>
          address.id === editFormik.values.id
            ? { ...address, ...addressData }
            : address
        )
      );
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditAddressClose = () => {
    setEditAddressOpen(false);
    setSelectedCustomer(null);
    editFormik.resetForm();
  };

  const handleDialogOpen = (type: string) => {
    setDialogType(type);
    setDialogOpen(true);
    handleClose();
  };

  const handleConfirm = async (status: string, reason: string) => {
    const companyId = Cookies.get(dataStorageKeywords.COMPANY_ID);

    try {
      if (dialogType === actionButtons.DELETE) {
        if (rowId) {
          deleteCustomer(rowId);
        }
        fetchCustomers();
      } else if (dialogType === actionButtons.UPDATE) {
        const response = await axiosTokenInstance.patch(
          apiUrls.CUSTOMER_ACTIVATION,
          {
            id: rowId,
            status: status,
            reason: status === CustomerStatus.SUSPENDED ? reason : null,
          }
        );
        showSnackbar(response.data.message, 'success');
        fetchCustomers();
      }
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setDialogOpen(false);
      setDialogType(null);
      setSelectedCustomer(null);
    }
  };

  const handleViewDialogOpen = async () => {
    try {
      const response = await axiosTokenInstance.get(
        apiUrls.GET_CUSTOMER_BY_ID,
        {
          params: { id: rowId },
        }
      );

      formik.setValues({
        name: response.data.name,
        customerCode: customerCode,
        organizationName: response.data.organizationName,
        countryCode: response.data.countryCode,
        telephoneNo: response.data.phoneNumber,
        email: response.data.email,
        status: status,
        reason: response.data.suspensionReason,
      });
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
    setViewDialogOpen(true);
  };

  const handleViewDialogClose = () => {
    setViewDialogOpen(false);
    setSelectedCustomer(null);
    formik.resetForm();
  };

  const fetchCustomerAddresses = async (customerId: string) => {
    try {
      const response = await axiosTokenInstance.get(
        apiUrls.GET_CUSTOMER_ADDRESSES,
        {
          params: { customerId },
        }
      );

      const addresses = response.data.map((addr: Address) => ({
        id: addr.id,
        contactPersonName: addr.contactPersonName,
        contactNo: addr.contactNo,
        addressLine1: addr.addressLine1,
        addressLine2: addr.addressLine2,
        city: addr.city,
        postCode: addr.postCode,
        country: addr.country,
        isDefault: addr.isDefault ?? false,
      }));

      setCustomerAddresses(addresses);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
      throw error;
    }
  };

  const handleAddressDialogOpen = async (customer: Customer) => {
    setSelectedCustomer(customer);
    try {
      await fetchCustomerAddresses(customer.id);
      setAddressDialogOpen(true);
    } catch {
      // Error already handled in fetchCustomerAddresses
    }
  };

  const handleAddressDialogClose = () => {
    setAddressDialogOpen(false);
    setSelectedCustomer(null);
  };

  const handleAddNewAddressDialogOpen = async (customer: Customer) => {
    setSelectedCustomer(customer);
    try {
      const response = await axiosTokenInstance.get(
        apiUrls.GET_CUSTOMER_BY_ID,
        {
          params: { id: rowId },
        }
      );

      addressFormik.setValues({
        ...customerAddressInitialValues,
        contactPersonName: response.data.name,
        countryCode: response.data.countryCode,
        contactNo: response.data.phoneNumber,
      });
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }

    setAddAddressDialogOpen(true);
  };

  const handleAddAddressDialogClose = () => {
    setAddAddressDialogOpen(false);
    addressFormik.resetForm();
  };

  const handleAddressFormSubmit = async () => {
    if (!selectedCustomer) return;

    try {
      const response = await axiosTokenInstance.post(apiUrls.ADD_ADDRESS, {
        contactPersonName: addressFormik.values.contactPersonName,
        contactNo: `+${addressFormik.values.countryCode} ${addressFormik.values.contactNo}`,
        addressLine1: addressFormik.values.addressLine1,
        addressLine2: addressFormik.values.addressLine2,
        postCode: addressFormik.values.postCode,
        city: addressFormik.values.city,
        country: addressFormik.values.country,
        customerId: selectedCustomer.id,
        companyId: Cookies.get(dataStorageKeywords.COMPANY_ID),
        isDefault: addressFormik.values.isDefault,
      });

      // Close dialog and reset form
      setAddAddressDialogOpen(false);
      addressFormik.resetForm();

      // Fetch address list
      await fetchCustomerAddresses(selectedCustomer.id);

      showSnackbar(response.data.message, 'success');
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleDefaultDialogOpen = (addressId: string) => {
    setSelectedAddressId(addressId);
    setDefaultDialogOpen(true);
  };

  const handleSetDefaultAddress = async () => {
    try {
      const response = await axiosTokenInstance.patch(
        apiUrls.SET_DEFAULT_ADDRESS,
        {
          id: selectedAddressId,
          customerId: selectedCustomer?.id,
        }
      );
      showSnackbar(response.data.message, 'success');
      setDefaultDialogOpen(false);

      setCustomerAddresses((prevAddresses) =>
        prevAddresses.map((addr) =>
          addr.id === selectedAddressId
            ? { ...addr, isDefault: true }
            : { ...addr, isDefault: false }
        )
      );
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleDeleteDialogOpen = (addressId: string) => {
    setSelectedAddressId(addressId);
    setDeleteDialogOpen(true);
  };

  const handleAddressDelete = async () => {
    try {
      const response = await axiosTokenInstance.delete(apiUrls.DELETE_ADDRESS, {
        params: { id: selectedAddressId },
      });
      showSnackbar(response.data.message, 'success');
      setDeleteDialogOpen(false);
      setCustomerAddresses((prevAddresses) =>
        prevAddresses.filter((address) => address.id !== selectedAddressId)
      );
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleCountryChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    addressFormik.setFieldValue('country', value);
    editFormik.setFieldValue('country', value);
  };

  const createAddressData = (formikValues: any) => {
    return {
      contactPersonName: formikValues.contactPersonName,
      contactNo: `+${formikValues.countryCode} ${formikValues.contactNo}`,
      addressLine1: formikValues.addressLine1,
      addressLine2: formikValues.addressLine2,
      postCode: formikValues.postCode,
      city: formikValues.city,
      country: formikValues.country,
    };
  };

  const formik = useFormik({
    initialValues: customerInitialValues,
    validationSchema: customerValidationSchema,
    onSubmit: handleEditFormSubmit,
  });

  const addressFormik = useFormik({
    initialValues: customerAddressInitialValues,
    validationSchema: customerAddressValidationSchema,
    onSubmit: handleAddressFormSubmit,
  });

  const editFormik = useFormik({
    initialValues: customerAddressInitialValues,
    validationSchema: customerAddressValidationSchema,
    onSubmit: handleEditAddressSubmit,
  });

  return (
    <>
      <ActionMenuButton
        onClick={handleClick}
        component="i"
        className="bx bx-dots-horizontal-rounded"
      />
      <ActionMenuPopover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItemsWrapper>
          <MenuItemWithIcon
            onClick={() => handleViewDialogOpen()}
            iconClassName="bx bx-file-find"
            text="View"
          />

          <ViewDialog
            height={
              formik.values.status === CustomerStatus.SUSPENDED
                ? '450px'
                : '380px'
            }
            open={viewDialogOpen}
            type="customer"
            handleClose={handleViewDialogClose}
            title={formik.values.name}
            titleinfo={customerDialogTitles.VIEW}
            subtitle={formik.values.customerCode}
            orgName={formik.values.organizationName}
            contactNo={formik.values.telephoneNo}
            status={formik.values.status}
            email={formik.values.email}
            reason={formik.values.reason}
            onSubmit={() => {
              // View-only dialog, no submit action needed
            }}
            smScreen={false}
            hideButton={true}
          >
            {selectedCustomer && <></>}
          </ViewDialog>

          <MenuItemWithIcon
            onClick={() =>
              handleEditDialogOpen({
                ...EMPTY_CUSTOMER,
                id: rowId,
                customerCode: customerCode,
              })
            }
            iconClassName="bx bx-edit"
            text="Edit"
          />

          <CustomDialog
            height={`${calculateDialogHeight(formik, [
              ['name', 'customerCode'],
              ['organizationName', 'telephoneNo'],
              ['email'],
            ])}px`}
            open={editDialogOpen}
            handleClose={handleEditDialogClose}
            title={customerDialogTitles.EDIT}
            subtitle={customerDialogSubtitles.EDIT}
            onSubmit={formik.handleSubmit}
            smScreen={false}
            buttonLabel="Update"
          >
            {selectedCustomer && (
              <FlexColumnBox>
                <FormFieldRow mt={2}>
                  <Box300Width>
                    <TextField id="name" label="Name" formikProps={formik} />
                  </Box300Width>

                  <Box300Width>
                    <TextField
                      id="customerCode"
                      label="Code"
                      formikProps={formik}
                    />
                  </Box300Width>
                </FormFieldRow>

                <FormFieldRow>
                  <Box300Width>
                    <TextField
                      id="organizationName"
                      label="Organization"
                      formikProps={formik}
                    />
                  </Box300Width>

                  <Box300Width>
                    <MobileField
                      id="telephoneNo"
                      label="Telephone Number"
                      formikProps={formik}
                      widthPercent="35%"
                      fieldName="telephoneNo"
                    />
                  </Box300Width>
                </FormFieldRow>

                <FormFieldRow>
                  <Box300Width>
                    <TextField id="email" label="Email" formikProps={formik} />
                  </Box300Width>
                </FormFieldRow>
              </FlexColumnBox>
            )}
          </CustomDialog>

          <MenuItemWithIcon
            onClick={() =>
              handleAddressDialogOpen({
                ...EMPTY_CUSTOMER,
                id: rowId,
                customerCode: customerCode,
              })
            }
            iconClassName="bx bx-buildings"
            text="Addresses"
          />

          <AddressBox
            open={addressDialogOpen}
            handleClose={handleAddressDialogClose}
            title={customerDialogTitles.VIEW_ADDRESSES}
            subtitle={customerDialogSubtitles.VIEW_ADDRESSES}
            contactPersonName={addressFormik.values.contactPersonName}
            contactNo={addressFormik.values.contactNo}
            addresses={customerAddresses}
            isDefault={addressFormik.values.isDefault}
            handleAddressDelete={handleDeleteDialogOpen}
            handleDefaultAddress={handleDefaultDialogOpen}
            handleAddressEdit={handleAddressEditOpen}
            onAddNewAddressClick={() => {
              if (selectedCustomer) {
                handleAddNewAddressDialogOpen(selectedCustomer);
              }
            }}
          >
            {selectedCustomer && <></>}
          </AddressBox>

          <CustomDialog
            height={`${getAddressDialogHeight()}px`}
            open={addAddressDialogOpen}
            handleClose={handleAddAddressDialogClose}
            title={customerDialogTitles.ADD_ADDRESS}
            subtitle={customerDialogSubtitles.ADD_ADDRESS}
            onSubmit={addressFormik.handleSubmit}
            smScreen={false}
            buttonLabel="Save"
          >
            <FlexColumnBox>
              <FormFieldRow mt={2}>
                <Box300Width>
                  <TextField
                    id="contactPersonName"
                    label="Contact Person Name"
                    formikProps={addressFormik}
                  />
                </Box300Width>

                <Box300Width>
                  <MobileField
                    id="contactNo"
                    label="Contact Number"
                    formikProps={addressFormik}
                    widthPercent="35%"
                    fieldName="contactNo"
                  />
                </Box300Width>
              </FormFieldRow>

              <FormFieldRow>
                <Box300Width>
                  <TextField
                    id="addressLine1"
                    label="Address line 1"
                    formikProps={addressFormik}
                  />
                </Box300Width>

                <Box300Width>
                  <TextField
                    id="addressLine2"
                    label="Address line 2"
                    formikProps={addressFormik}
                  />
                </Box300Width>
              </FormFieldRow>

              <FormFieldRow>
                <Box300Width>
                  <TextField
                    id="city"
                    label="City"
                    formikProps={addressFormik}
                  />
                </Box300Width>

                <Box300Width>
                  <TextField
                    id="postCode"
                    label="Postal Code"
                    formikProps={addressFormik}
                  />
                </Box300Width>
              </FormFieldRow>

              <BoxWithMarginBottom justifyContent="space-between" gap={2}>
                <CountryPicker
                  width="300px"
                  label="Country"
                  value={addressFormik.values.country}
                  onChange={handleCountryChange}
                  error={addressFormik.errors.country}
                  touched={addressFormik.touched.country}
                />
              </BoxWithMarginBottom>
            </FlexColumnBox>
          </CustomDialog>

          <CustomDialog
            height={`${getAddressDialogHeight()}px`}
            open={editAddressOpen}
            handleClose={handleEditAddressClose}
            title={customerDialogTitles.EDIT_ADDRESS}
            subtitle={customerDialogSubtitles.EDIT_ADDRESS}
            onSubmit={editFormik.handleSubmit}
            smScreen={false}
            buttonLabel="Update"
          >
            <FlexColumnBox>
              <FormFieldRow mt={2}>
                <Box300Width>
                  <TextField
                    id="contactPersonName"
                    label="Contact Person Name"
                    formikProps={editFormik}
                  />
                </Box300Width>
                <Box300Width>
                  <MobileField
                    id="contactNo"
                    label="Contact Number"
                    formikProps={editFormik}
                    widthPercent="35%"
                    fieldName="contactNo"
                  />
                </Box300Width>
              </FormFieldRow>

              <FormFieldRow>
                <Box300Width>
                  <TextField
                    id="addressLine1"
                    label="Address line 1"
                    formikProps={editFormik}
                  />
                </Box300Width>
                <Box300Width>
                  <TextField
                    id="addressLine2"
                    label="Address line 2"
                    formikProps={editFormik}
                  />
                </Box300Width>
              </FormFieldRow>

              <FormFieldRow>
                <Box300Width>
                  <TextField id="city" label="City" formikProps={editFormik} />
                </Box300Width>
                <Box300Width>
                  <TextField
                    id="postCode"
                    label="Postal Code"
                    formikProps={editFormik}
                  />
                </Box300Width>
              </FormFieldRow>

              <BoxWithMarginBottom justifyContent="space-between" gap={2}>
                <CountryPicker
                  width="300px"
                  label="Country"
                  value={editFormik.values.country}
                  onChange={handleCountryChange}
                  error={editFormik.errors.country}
                  touched={editFormik.touched.country}
                />
              </BoxWithMarginBottom>
            </FlexColumnBox>
          </CustomDialog>

          <ConfirmationDialog
            height="275px"
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            onConfirm={handleAddressDelete}
            title={customerDialogTitles.DELETE_ADDRESS}
            subtitle={customerDialogSubtitles.DELETE_ADDRESS}
            confirmText="Delete"
            confirmButtonColor="var(--error)"
            alertIconColor="var(--error)"
          />

          <ConfirmationDialog
            height="275px"
            open={defaultDialogOpen}
            onClose={() => setDefaultDialogOpen(false)}
            onConfirm={handleSetDefaultAddress}
            title={customerDialogTitles.SET_DEFAULT_ADDRESS}
            subtitle={customerDialogSubtitles.SET_DEFAULT_ADDRESS}
            confirmText="Update"
            confirmButtonColor="var(--warning)"
            alertIconColor="var(--warning)"
          />

          <MenuItemWithIcon
            onClick={() => handleDialogOpen(actionButtons.UPDATE)}
            iconClassName="bx bx-refresh"
            text="Update Status"
          />

          <MenuItemWithIcon
            onClick={() => handleDialogOpen(actionButtons.DELETE)}
            iconClassName="bx bx-trash"
            text="Delete"
          />
        </MenuItemsWrapper>
      </ActionMenuPopover>

      <ConfirmationDialog
        height={dialogType === actionButtons.DELETE ? '275px' : '352px'}
        open={dialogOpen}
        reasonHeight="100px"
        rows={3}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        title={
          dialogType === actionButtons.DELETE
            ? customerDialogTitles.DELETE
            : customerDialogTitles.UPDATE_STATUS
        }
        subtitle={
          dialogType === actionButtons.DELETE
            ? customerDialogSubtitles.DELETE
            : customerDialogSubtitles.UPDATE_STATUS
        }
        confirmText={capitalizeFirstLetter(dialogType || '')}
        confirmButtonColor={
          dialogType === actionButtons.DELETE
            ? 'var(--error)'
            : newStatus === CustomerStatus.ACTIVE
            ? 'var(--success)'
            : 'var(--error)'
        }
        alertIconColor={
          dialogType === actionButtons.DELETE ? 'var(--error)' : 'var(--error)'
        }
        dialogType={
          dialogType === actionButtons.UPDATE ? dialogType : undefined
        }
        status={status}
        onStatusChange={handleStatusChange}
        statusOptions={customerStatusOptions.map((option) => option.value)}
        reason={internalSuspensionReason}
      />
    </>
  );
};
export default CustomerActionMenuButton;
